import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../store/redux/store/store';
import {
  getCompanyRules,
  updateCompanyRules,
  operatorUpdateCompanyRules,
} from '../../../../api/General/Company';
import { updateCompanyRules as updateCompanyRulesAction } from '../../../../store/redux/actions/userActionCreator';
import type { CompanyRulesInput } from '../../../../models/general';
import { Switch } from '../../../../components/ui/Switch';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import { cn } from '../../../../utils/cn';

/**
 * CO-240 — HR-facing enablement for the Engagement & Performance background jobs.
 *
 * Each toggle decides whether a job RUNS AND EMITS for this company at all. That is a different
 * question from the Company Defaults tab next door, which decides which CHANNELS a notification is
 * allowed to use once it exists — silencing every channel there still leaves the job computing.
 *
 * 🔴 THE POST SENDS THE WHOLE CompanyRulesView BACK, NOT JUST THESE FOUR FLAGS. CompanyRulesInput
 * has non-nullable members (attendanceImageConfig, breakConfig, timesheetCreationDay, the
 * scheduledShift* thresholds …) that CompanyService.UpdateCompanyRules assigns UNCONDITIONALLY.
 * A four-field POST would deserialize those to their C# defaults and silently wipe the tenant's
 * attendance configuration. Same round-trip pattern as Settings/Attendance/workscheduleconfig.tsx.
 * Only the four booleans below are ever mutated locally.
 *
 * OWNER DECISIONS APPLIED HERE — cite the SECTION, never a bare ordinal ("decision 11" means three
 * different things in this project):
 *
 *  §14.1c/14 — the re-GET immediately before the POST stays on BOTH surfaces (this HR tab and the
 *              Operator route). It is not a defect and must not be reverted "to restore the old
 *              behaviour"; the owner accepted its failure mode to close the lost-update window for
 *              HR as well. See handleSave.
 *  §14.1c/15 — Company.EnableAINudges is OPERATOR-WRITABLE. An earlier build made it read-only on
 *              the Operator surface and stripped it from the POST body; because Q12NudgeJob and
 *              RecognitionGapReminderJob both AND on that flag, that left two of the three toggles
 *              unable to send at all for any tenant with EnableAINudges=false — a greyed switch and
 *              a "Paused" chip with no remedy. That lock is REVERSED: the master writes from both
 *              surfaces, through the same `if (input.EnableAINudges.HasValue)` branch in
 *              CompanyService.UpdateCompanyRules (CompanyService.cs, General/) that the HR tab uses.
 *              The blast radius (EnableAINudges defaults TRUE and gates the whole AI-nudge family)
 *              is answered by making the cross-tenant write VISIBLE at the switch, not by disabling
 *              it — see the "Operator action" / "Unsaved changes" markers on the master header.
 */

type JobFlagKey = 'enableRecognitionGapReminder' | 'enableQ12Nudge' | 'enableCheckInReminder';

interface EngagementFlags {
  enableAINudges: boolean;
  enableRecognitionGapReminder: boolean;
  enableQ12Nudge: boolean;
  enableCheckInReminder: boolean;
}

const EMPTY_FLAGS: EngagementFlags = {
  enableAINudges: false,
  enableRecognitionGapReminder: false,
  enableQ12Nudge: false,
  enableCheckInReminder: false,
};

export interface EngagementJobsTabProps {
  /**
   * PRESENCE of this prop switches the tab to the OPERATOR cross-tenant surface (owner decision
   * §14.1c/11). When set the tab reads and writes THIS company instead of the signed-in user's own,
   * and posts through `OperatorUpdateCompanyRules` (Policy = "Operator", CompanyController.cs:283)
   * instead of `UpdateCompanyRules` (Policy = "Hr", CompanyController.cs:237, which 403s a pure
   * Operator even on their own tenant). Absent => the HR tab, behaving exactly as CO-240 shipped it.
   */
  companyId?: number;
}

const EngagementJobsTab: React.FC<EngagementJobsTabProps> = ({ companyId: operatorCompanyId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ownCompanyId = useSelector((state: RootState) => state.user.company?.id);

  // The mode switch keys on the PROP, not on "is this a different tenant". An Operator editing their
  // OWN tenant through the picker (info@mowazaf.com sits in company 17) must still post through the
  // Operator route, because the HR route's policy refuses a pure Operator regardless of tenant.
  const isOperatorSurface = operatorCompanyId != null;
  const companyId = operatorCompanyId ?? ownCompanyId;

  // 🔴 THE COPY MODE IS DERIVED FROM `isOperatorSurface`, NOT FROM A SECOND PROP — DELIBERATELY.
  // "Which tenant does this page speak about" is the SAME question as "which write route does it
  // post through", and the file already answers it once. A separate `operatorCopy` prop would be a
  // second source of truth for one fact, and — since the caller would have to remember to pass it —
  // its default would silently leave the operator route reading "your company". That is exactly how
  // the reversed `aiNudgesReadOnly` prop went wrong. One discriminator, no way to get it half-set.
  const ownTenantCopy = !isOperatorSurface;

  // The app's i18n runs with keySeparator:false and the DEFAULT nsSeparator ':', so any English
  // key containing a colon ('1:1 check-in reminder', or a sentence with a colon in it) is read as
  // namespace + key and renders EMPTY. Every lookup on this tab goes through tx so a later copy
  // edit cannot reintroduce that. Same fix as NotificationPreferenceMatrix.getTypeLabel.
  const tx = useCallback((key: string) => t(key, { nsSeparator: false }), [t]);

  const [rules, setRules] = useState<CompanyRulesInput | null>(null);
  const [flags, setFlags] = useState<EngagementFlags>(EMPTY_FLAGS);
  const [baseline, setBaseline] = useState<EngagementFlags>(EMPTY_FLAGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  /**
   * THE FOURTH STATE. A company with no CompanyRules row is SKIPPED by all three jobs: every sweep
   * gates on a POSITIVE EXISTS over that table (Q12NudgeJob.cs:102, RecognitionGapReminderJob.cs:90,
   * CheckInReminderJob.cs:76), so a missing row is absence of permission, not a default. It has to
   * render AS skipped. Rendering it as three innocent OFF switches would misdescribe it, and letting
   * it fall through to the generic load error would surface the fail-closed property as a mystery:
   * CompanyService.UpdateCompanyRules returns NotFound for such a company, so a Save button here
   * could only ever fail after the click.
   */
  const [noRulesRow, setNoRulesRow] = useState(false);

  const fetchRules = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSaveSuccess(false);
    setNoRulesRow(false);
    try {
      const result = await getCompanyRules(companyId);
      if (result.errors?.length) {
        // 🔴 FAIL CLOSED, AND CLEAR THE FORM WITH IT. Setting only `error` used to leave rules=null,
        // noRulesRow=false and the flags at EMPTY_FLAGS while the render gate was
        // `companyId && !noRulesRow` — so the whole switch panel painted UNDER the error banner with
        // all four switches OFF. On the Operator surface that is a confident all-OFF picture of a
        // tenant whose real configuration was never read, and toggling it armed a Save button whose
        // handler early-returned before setSaving(true): no request, no error, no spinner, no toast.
        // The render gate now requires `rules`, and this clears every field the gate and the payload
        // read from, so a failed GET can never be mistaken for a loaded row.
        console.error(
          'Failed to load engagement job settings (companyId=%s):',
          companyId,
          result.errors
        );
        setRules(null);
        setFlags(EMPTY_FLAGS);
        setBaseline(EMPTY_FLAGS);
        setError(result.errors[0]?.description || tx('Failed to load settings'));
        return;
      }
      if (!result.payload) {
        // 200 with a null payload = this company has no CompanyRules row. `rules` is cleared as well
        // as flagged, so a stale object from a previously selected company can never reach a POST.
        setRules(null);
        setNoRulesRow(true);
        return;
      }
      const payload = result.payload;
      const loaded: EngagementFlags = {
        enableAINudges: !!payload.enableAINudges,
        enableRecognitionGapReminder: !!payload.enableRecognitionGapReminder,
        enableQ12Nudge: !!payload.enableQ12Nudge,
        enableCheckInReminder: !!payload.enableCheckInReminder,
      };
      setRules(payload as unknown as CompanyRulesInput);
      setFlags(loaded);
      setBaseline(loaded);
    } catch (err) {
      console.error('Failed to load engagement job settings:', err);
      setError(tx('Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [companyId, tx]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const hasChanges = useMemo(
    () => (Object.keys(baseline) as (keyof EngagementFlags)[]).some((k) => baseline[k] !== flags[k]),
    [baseline, flags]
  );

  const setFlag = useCallback((key: keyof EngagementFlags, value: boolean) => {
    setFlags((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  }, []);

  const handleSave = async () => {
    if (!companyId || !rules || noRulesRow) {
      // 🔴 A PRECONDITION, NOT A CONTROL PATH — AND IT RECORDS THAT IT FIRED. The render gate below
      // only mounts this button when the same three facts hold, so reaching here means the gate and
      // this guard disagreed. It used to `return` in silence BEFORE setSaving(true): the user got no
      // request, no error, no spinner and no toast from a click on an enabled button. If it ever
      // fires again it now leaves a console record and a visible failure instead of nothing.
      console.error(
        'Engagement jobs save blocked by precondition: companyId=%s rules=%s noRulesRow=%s',
        companyId,
        rules ? 'loaded' : 'null',
        noRulesRow
      );
      setError(tx('Failed to save settings'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 🔴 RE-GET IMMEDIATELY BEFORE THE POST and overlay only the four booleans onto that FRESH row.
      // The POST carries the WHOLE CompanyRulesView (see the file header), so a snapshot taken at page
      // load is a lost-update generator: Settings/Attendance/workscheduleconfig.tsx and
      // Settings/Companies/OperatorCompanyRules.tsx each hold their own load-time snapshot of this same
      // row and also post the whole DTO, so a stale body here silently reverts whatever they saved in
      // between. On the Operator surface the window is worse, because the human never loaded the target
      // tenant's other settings at all and would not recognise a reverted value if they saw one.
      const fresh = await getCompanyRules(companyId);
      if (fresh.errors?.length || !fresh.payload) {
        // ABORT. Never fall back to the stale `rules`: on the Operator surface that object may belong
        // to a DIFFERENT company, and posting it would overwrite this tenant's entire configuration.
        console.error(
          'Engagement jobs save aborted — pre-POST re-GET failed (companyId=%s):',
          companyId,
          fresh.errors ?? 'null payload'
        );
        setError(fresh.errors?.[0]?.description || tx('Failed to load settings'));
        return;
      }
      const current = fresh.payload as unknown as CompanyRulesInput;
      // 🔴 §14.1c/15 — ALL FOUR FLAGS RIDE THE POST, INCLUDING enableAINudges, ON BOTH SURFACES.
      // Do not reintroduce a `delete input.enableAINudges` here: omitting it hits the server's
      // `if (input.EnableAINudges.HasValue)` guard as "absent", which silently leaves the stored
      // value alone — so the master would read as writable and never write, and the two AI jobs
      // would sit "Paused" with no reachable remedy on any tenant whose flag is already false.
      const input: CompanyRulesInput = { ...current, ...flags };
      const writer = isOperatorSurface ? operatorUpdateCompanyRules : updateCompanyRules;
      const result = await writer(companyId, input);
      if (result.errors?.length) {
        setError(result.errors[0]?.description || tx('Failed to save settings'));
        return;
      }
      setRules(current);
      if (!isOperatorSurface) {
        // Keep the Redux company-rules copy in sync so dependent surfaces react without a reload.
        // id/companyId are stripped so they can't overwrite company.id.
        //
        // 🔴 DELIBERATELY NOT DISPATCHED ON THE OPERATOR SURFACE. Do not re-add it "for consistency".
        // This slice is the SIGNED-IN user's OWN company rules. Feeding it the TARGET tenant's row
        // would make every rules-driven surface in the operator's own session (attendance thresholds,
        // break config, notification mutes) start behaving like the company they were merely
        // configuring, with no visible cause and no reload to clear it.
        const rulesOnly: Partial<CompanyRulesInput> = { ...input };
        delete rulesOnly.id;
        delete rulesOnly.companyId;
        dispatch(updateCompanyRulesAction(rulesOnly));
      }
      setBaseline(flags);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save engagement job settings:', err);
      setError(tx('Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  /**
   * "A company was asked for and no row came back" — the state the form must NOT paint over.
   * Distinct from `noRulesRow` (a 200 whose payload was null: the company genuinely has no
   * CompanyRules row, which the jobs treat as skipped and which renders its own section) and from
   * `!companyId` (nothing was asked for). Reachable only after `loading` is false, so it cannot
   * flicker on during the first fetch.
   */
  const loadFailed = !!companyId && !rules && !noRulesRow;

  /** One job row: name, what it sends, and the three facts HR needs before flipping it on. */
  const renderJob = (job: {
    key: JobFlagKey;
    name: string;
    summary: string;
    sends: string;
    audience: string;
    cadence: string;
    requiresAiNudges: boolean;
    note?: string;
  }) => {
    const on = flags[job.key];
    const paused = on && job.requiresAiNudges && !flags.enableAINudges;
    const active = on && !paused;
    const switchId = `engagement-job-${job.key}`;

    return (
      <div
        key={job.key}
        className={cn(
          'px-4 py-4 transition-colors duration-200 sm:px-5',
          active && 'bg-primary/[0.04]',
          paused && 'bg-amber-500/[0.06]'
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor={switchId}
                className="cursor-pointer text-sm font-semibold text-foreground"
              >
                {tx(job.name)}
              </label>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                  active && 'bg-primary/10 text-primary',
                  paused && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
                  !on && 'bg-muted text-muted-foreground'
                )}
              >
                {active ? tx('Sending') : paused ? tx('Paused') : tx('Not sending')}
              </span>
            </div>
            <p className="mt-1.5 max-w-[68ch] text-sm text-muted-foreground">{tx(job.summary)}</p>
            <dl className="mt-2.5 flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">{tx('Sends')}</dt>
                <dd className="font-medium text-foreground">{tx(job.sends)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">{tx('Goes to')}</dt>
                <dd className="font-medium text-foreground">{tx(job.audience)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">{tx('Runs')}</dt>
                <dd className="font-medium text-foreground">{tx(job.cadence)}</dd>
              </div>
            </dl>
            {job.requiresAiNudges && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <AppIcon name="CornerLeftUp" size={13} className="shrink-0 rtl:-scale-x-100" />
                {paused
                  ? tx('Held back because AI nudges are off for this company.')
                  : tx('Only sends while AI nudges are on for this company.')}
              </p>
            )}
            {job.note && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <AppIcon name="Info" size={13} className="shrink-0" />
                {tx(job.note)}
              </p>
            )}
          </div>
          <Switch
            id={switchId}
            checked={on}
            onCheckedChange={(v) => setFlag(job.key, v)}
            disabled={saving}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* What this tab does, and how it differs from the tab beside it.
          🔴 OWN-TENANT COPY ONLY. Both sentences are FALSE on the Operator route and are therefore
          not rendered there: (1) "runs for your company at all" — the operator is configuring a
          DIFFERENT tenant, and this is the one surface where mis-targeting is the whole risk;
          (2) "Company Defaults, on the tab beside this one" — the operator route is standalone,
          there is no tab beside it, and Company Defaults is a page the Operator is DENIED
          (SETTINGS_ROLES is Hr/Admin). Suppressing a false statement invents nothing; REWRITING it
          would need new copy, and per the i18n rule no ar/fr may be hand-authored. The tenant-neutral
          replacement wording is reported as BLOCKED — see blockedOn in the handoff. Everything the
          operator still needs is carried by the per-job rows below ("Sends" / "Goes to" / "Runs" and
          the AI-nudge dependency line), all of which already say "this company", not "your company". */}
      {ownTenantCopy && (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <AppIcon name="Info" size={18} className="mt-0.5 shrink-0 text-primary" />
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                {tx(
                  'These switches decide whether each engagement reminder runs for your company at all. While a job is off it sends nothing and creates nothing.'
                )}
              </p>
              <p>
                {tx(
                  'Company Defaults, on the tab beside this one, is a separate control: it decides which channels a reminder may use once it has been created.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Retry appears ONLY in the failed-load state, and that is deliberate. When the load failed
          there is no form and therefore no Reset button, so without this the state is terminal —
          the operator's only way out is a browser reload. When the form IS rendered (a SAVE failed,
          say, or the pre-POST re-GET aborted) Retry is suppressed, because fetchRules discards the
          user's pending toggles: offering it there would read as "retry the save" and silently throw
          the edits away. In that case Reset — which says what it does — is already on screen. */}
      {error && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <span className="flex min-w-0 items-center gap-2">
            <AppIcon name="AlertCircle" size={16} className="shrink-0" />
            {error}
          </span>
          {loadFailed && (
            <Button variant="outline" size="sm" onClick={fetchRules} className="ms-auto">
              {tx('Retry')}
            </Button>
          )}
        </div>
      )}

      {/* The fourth state, drawn with keys that already resolve in en, ar AND fr: no new strings, so
          no hand-authored Arabic or French. Naming the three jobs beside a Skipped chip says what the
          missing row MEANS (these three send nothing) instead of leaving the operator to infer it,
          and there is deliberately no Save button, because there is no row to save into. */}
      {noRulesRow && (
        <section className="rounded-lg border border-border bg-muted/30 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <AppIcon
              name="AlertCircle"
              size={18}
              className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <div className="min-w-0 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{tx('Engagement Jobs')}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                  {tx('Not set')}
                </span>
              </div>
              <ul className="space-y-1.5">
                {(['Recognition gap reminder', 'Team engagement nudge', '1:1 check-in reminder'] as const).map(
                  (name) => (
                    <li key={name} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                      <span>{tx(name)}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                        {tx('Skipped')}
                      </span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </section>
      )}

      {!companyId && !error && (
        <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
          {tx('No company is selected, so engagement jobs cannot be configured.')}
        </div>
      )}

      {/* 🔴 `rules` IS PART OF THE GATE, AND IT IS THE LOAD-BEARING PART. It is non-null only after a
          GET that came back with a payload, so a failed load, a company with no CompanyRules row, and
          a not-yet-loaded remount all render NO panel and NO Save button. A control that cannot act
          must never look like it can: the Save handler's precondition is `!companyId || !rules ||
          noRulesRow`, and this gate is now exactly its negation, so the button can no longer be
          rendered enabled over a handler that would early-return. Do not relax this back to
          `companyId && !noRulesRow`. */}
      {companyId && rules && !noRulesRow && (
        <>
          {/* Group 1 — the two AI nudges. Containment plus the header switch is what makes the
              dependency legible: the master lives at the top of the same frame the dependants sit in. */}
          <section className="overflow-hidden rounded-lg border border-border">
            <header
              className={cn(
                'flex items-start justify-between gap-4 border-b border-border px-4 py-4 transition-colors duration-200 sm:px-5',
                flags.enableAINudges ? 'bg-primary/[0.06]' : 'bg-muted/40'
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="engagement-master-ai-nudges"
                    className="cursor-pointer text-sm font-semibold text-foreground"
                  >
                    {tx('AI nudges')}
                  </label>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                      flags.enableAINudges
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {flags.enableAINudges ? tx('Enabled') : tx('Disabled')}
                  </span>
                  {/* 🔴 §14.1c/15 — THE CONSEQUENCE, MADE VISIBLE INSTEAD OF LOCKED AWAY. This switch
                      writes Company.EnableAINudges for ANOTHER tenant, and that column AND-gates the
                      whole AI-nudge family, so one click here can un-mute or mute every AI nudge that
                      tenant receives. The owner took that blast radius knowingly and rejected a lock,
                      so the control stays LIVE and is MARKED instead — amber, beside the state chip,
                      at the point of action rather than as prose somewhere above the row.
                      Both strings already resolve in en, ar AND fr (common.json "Operator action",
                      common.json "Unsaved changes"); nothing here is hand-authored. A fuller warning
                      naming the effect in words is reported as BLOCKED — no such key exists in ar/fr. */}
                  {isOperatorSurface && (
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium leading-4 text-amber-700 dark:text-amber-400">
                      {tx('Operator action')}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 max-w-[68ch] text-sm text-muted-foreground">
                  {tx(
                    'The company-wide switch for automatically generated coaching prompts. Turning it off stops both reminders below, whatever their own switch says.'
                  )}
                </p>
                {isOperatorSurface && flags.enableAINudges !== baseline.enableAINudges && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    <AppIcon name="AlertCircle" size={13} className="shrink-0" />
                    {tx('Unsaved changes')}
                  </p>
                )}
              </div>
              {/* §14.1c/15 — WRITABLE. `disabled` is saving-only: no `lockAINudges`, no operator
                  carve-out. Re-adding one would re-break both AI jobs on every tenant whose
                  EnableAINudges is already false, since they AND on this flag. */}
              <Switch
                id="engagement-master-ai-nudges"
                checked={flags.enableAINudges}
                onCheckedChange={(v) => setFlag('enableAINudges', v)}
                disabled={saving}
              />
            </header>

            <div className="divide-y divide-border">
              {renderJob({
                key: 'enableRecognitionGapReminder',
                name: 'Recognition gap reminder',
                summary:
                  'Prompts a manager to look at which of their people have gone a long time without recognition, and links straight to the gaps view.',
                sends: 'Email and in-app',
                audience: 'Anyone with at least one direct report',
                cadence: 'Every second Monday',
                requiresAiNudges: true,
              })}
              {renderJob({
                key: 'enableQ12Nudge',
                name: 'Team engagement nudge',
                summary:
                  'When a department scores below 40 on an engagement dimension, its managers get one short prompt naming the dimension and a suggested next step.',
                sends: 'In-app and push',
                audience: 'Managers of the department that scored low',
                cadence: 'Every Monday',
                requiresAiNudges: true,
              })}
            </div>
          </section>

          {/* Group 2 — deliberately separate: this one is a scheduling reminder, not an AI nudge,
              so it is unaffected by the master switch above. */}
          <section className="overflow-hidden rounded-lg border border-border">
            <header className="border-b border-border bg-muted/40 px-4 py-3 sm:px-5">
              <h3 className="text-sm font-semibold text-foreground">{tx('Runs independently')}</h3>
              <p className="mt-1 max-w-[68ch] text-sm text-muted-foreground">
                {tx('Not an AI nudge, so the switch above does not affect it.')}
              </p>
            </header>
            <div>
              {renderJob({
                key: 'enableCheckInReminder',
                name: '1:1 check-in reminder',
                summary:
                  'Reminds an employee about the check-in booked with their manager for that day, and sends the manager one digest listing every check-in they have that day.',
                sends: 'Email and in-app',
                audience: 'The employee, plus a daily digest to their manager',
                cadence: 'On the day of the check-in, about an hour after the shift starts',
                requiresAiNudges: false,
                note: 'Available on paid plans only.',
              })}
            </div>
          </section>

          {/* 🔴 OWN-TENANT COPY ONLY, for the same reason as the intro block. Its second half sends
              the reader to Company Defaults — a page the Operator is DENIED (SETTINGS_ROLES is
              Hr/Admin) — so on the operator route it is advice that cannot be followed. Its FIRST
              half ("a change applies from the next scheduled run") is true and useful on both
              surfaces, but it is not a separate key and cannot be shown alone without new copy in
              en/ar/fr. Splitting it is reported as BLOCKED rather than hand-authored. */}
          {ownTenantCopy && (
            <p className="text-xs text-muted-foreground">
              {tx(
                'A change applies from the next scheduled run. If a reminder still does not arrive, check that its channels are not disabled under Company Defaults.'
              )}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            {saveSuccess && (
              <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                <AppIcon name="Check" size={16} />
                {tx('Settings saved successfully')}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={fetchRules} disabled={saving || !hasChanges}>
              {tx('Reset')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !hasChanges} loading={saving}>
              {tx('Save Changes')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EngagementJobsTab;
